import { useState, useEffect, useRef } from 'react'

const REACTIONS = [
  { id: 'needs-work', label: 'Needs work', emoji: '😕' },
  { id: 'okay', label: "It's okay", emoji: '😐' },
  { id: 'love', label: 'Love it', emoji: '😍' },
]

export function FeedbackModal({ isOpen, onClose }) {
  const [reaction, setReaction] = useState(null)
  const [comment, setComment] = useState('')
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState('idle') // idle, sending, sent, error
  const modalRef = useRef(null)

  useEffect(() => {
    function handleClickOutside(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onClose()
      }
    }
    function handleEscape(e) {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleEscape)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
        document.removeEventListener('keydown', handleEscape)
      }
    }
  }, [isOpen, onClose])

  const handleSubmit = async (e) => {
    e.preventDefault()

    setStatus('sending')

    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_KEY,
          subject: 'New Feedback from arisk.ai',
          from_name: 'arisk.ai Feedback',
          reaction: reaction || 'None',
          comment: comment.trim() || 'None',
          email: email.trim() || 'None',
        }),
      })

      if (response.ok) {
        setStatus('sent')
        setReaction(null)
        setComment('')
        setEmail('')
        setTimeout(() => {
          onClose()
          setStatus('idle')
        }, 2000)
      } else {
        setStatus('error')
      }
    } catch {
      setStatus('error')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        ref={modalRef}
        className="bg-white dark:bg-neutral-800 rounded-lg shadow-xl w-full max-w-md"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-neutral-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Feedback
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-neutral-500 hover:text-gray-600 dark:hover:text-neutral-300"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {status === 'sent' ? (
            <div className="text-center py-8">
              <div className="text-green-600 dark:text-green-400 text-4xl mb-2">✓</div>
              <p className="text-gray-700 dark:text-neutral-300">Thanks for your feedback!</p>
            </div>
          ) : (
            <>
              {/* Quick Reactions */}
              <div>
                <label className="block text-sm text-gray-700 dark:text-neutral-300 mb-3">
                  How are we doing?
                </label>
                <div className="flex gap-2">
                  {REACTIONS.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      onClick={() => setReaction(r.id)}
                      className={`flex-1 py-3 px-2 rounded-lg border text-center transition ${
                        reaction === r.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-200 dark:border-neutral-700 hover:border-gray-300 dark:hover:border-neutral-600'
                      }`}
                    >
                      <div className="text-2xl mb-1">{r.emoji}</div>
                      <div className="text-xs text-gray-600 dark:text-neutral-400">{r.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Comment */}
              <div>
                <label className="block text-sm text-gray-700 dark:text-neutral-300 mb-2">
                  Anything else? <span className="text-gray-400 dark:text-neutral-500">(optional)</span>
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Tell us more..."
                  className="w-full p-3 bg-gray-50 dark:bg-neutral-800/50 border border-gray-200 dark:border-neutral-700 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 dark:text-neutral-300 placeholder-gray-400 dark:placeholder-neutral-500"
                  rows={3}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm text-gray-700 dark:text-neutral-300 mb-2">
                  Email <span className="text-gray-400 dark:text-neutral-500">(optional)</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full p-3 bg-gray-50 dark:bg-neutral-800/50 border border-gray-200 dark:border-neutral-700 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700 dark:text-neutral-300 placeholder-gray-400 dark:placeholder-neutral-500"
                />
              </div>

              {status === 'error' && (
                <p className="text-sm text-red-600 dark:text-red-400">Something went wrong. Please try again.</p>
              )}

              <button
                type="submit"
                disabled={status === 'sending' || (!reaction && !comment.trim())}
                className="w-full py-2.5 px-4 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-200 dark:disabled:bg-neutral-700 disabled:cursor-not-allowed rounded-lg text-white font-medium transition"
              >
                {status === 'sending' ? 'Sending...' : 'Send'}
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  )
}
